import Plugin from '@ckeditor/ckeditor5-core/src/plugin'
import AttributeElement from '@ckeditor/ckeditor5-engine/src/view/attributeelement'
import DocumentFragment from '@ckeditor/ckeditor5-engine/src/view/documentfragment'
import Node from '@ckeditor/ckeditor5-engine/src/view/node'
import ViewPosition from '@ckeditor/ckeditor5-engine/src/view/position'
import Range from '@ckeditor/ckeditor5-engine/src/model/range'
import { ContextualBalloon, CssTransitionDisablerMixin } from '@ckeditor/ckeditor5-ui'
import clickOutsideHandler from '@ckeditor/ckeditor5-ui/src/bindings/clickoutsidehandler'
import Writer from '@ckeditor/ckeditor5-engine/src/model/writer'
import ModelPosition from '@ckeditor/ckeditor5-engine/src/model/position'
import { Options } from '@ckeditor/ckeditor5-utils/src/dom/position'
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview'
import ClickObserver from '@ckeditor/ckeditor5-engine/src/view/observer/clickobserver'
import { TreeWalkerValue } from '@ckeditor/ckeditor5-engine/src/view/treewalker'
import type Item from '@ckeditor/ckeditor5-engine/src/view/item'
import { t } from '@ulule/localize'

import { AnchorActionView } from './AnchorActionView'
import { AnchorCommand } from './AnchorCommand'
import { UnanchorCommand } from './UnanchorCommand'
import { AnchorFormView } from './AnchorFormView'

import anchorIcon from './theme/icons/anchor.svg'

const VISUAL_SELECTION_MARKER_NAME = 'anchor-ui'
const LINK_KEYSTROKE = 'Ctrl+M'

export class AnchorUI extends Plugin {
  private _balloon?: ContextualBalloon
  private _actionView?: AnchorActionView
  private _formView?: {
    disableCssTransitions(): void
    enableCssTransitions(): void
  } & AnchorFormView

  static get requires() {
    return [ContextualBalloon]
  }

  static get pluginName(): string {
    return 'AnchorUI'
  }

  init(): void {
    const editor = this.editor

    editor.editing.view.addObserver(ClickObserver)

    this._actionView = this._createActionView()
    this._formView = this._createFormView()
    this._balloon = editor.plugins.get(ContextualBalloon)

    this._createToolbarAnchorButton()
    this._enableUserBalloonInteractions()

    // Renders a fake visual selection marker on an expanded selection.
    editor.conversion.for('editingDowncast').markerToHighlight({
      model: VISUAL_SELECTION_MARKER_NAME,
      view: {
        classes: ['ck-fake-anchor-selection'],
      },
    })

    // Renders a fake visual selection marker on a collapsed selection.
    editor.conversion.for('editingDowncast').markerToElement({
      model: VISUAL_SELECTION_MARKER_NAME,
      view: {
        name: 'span',
        classes: ['ck-fake-anchor-selection', 'ck-fake-anchor-selection_collapsed'],
      },
    })
  }

  get _isActionInPanel(): boolean {
    return this._balloon?.hasView(this._actionView!) ?? false
  }

  get _isFormInPanel(): boolean {
    return this._balloon?.hasView(this._formView!) ?? false
  }

  get _isUIInPanel(): boolean {
    return this._isFormInPanel || this._isActionInPanel
  }

  get _isActionVisible(): boolean {
    return this._balloon?.visibleView === this._actionView
  }

  get _isUIVisible(): boolean {
    const visibleView = this._balloon?.visibleView

    return visibleView == this._formView || this._isActionVisible
  }

  /**
   * Creates a toolbar Anchor button. Clicking this button will show
   * a {@link #_balloon} attached to the selection.
   */
  private _createToolbarAnchorButton(): void {
    const editor = this.editor
    const anchorCommand = editor.commands.get('anchor') as AnchorCommand

    // Handle the `Ctrl+M` keystroke and show the panel.
    editor.keystrokes.set(LINK_KEYSTROKE, (keyEvtData, cancel) => {
      // Prevent focusing the search bar in FF, Chrome and Edge. See https://github.com/ckeditor/ckeditor5/issues/4811.
      cancel()

      if (anchorCommand.isEnabled) {
        this._showUI(true)
      }
    })

    editor.ui.componentFactory.add('anchor', (locale) => {
      const button = new ButtonView(locale)

      button.isEnabled = true
      button.label = t('Anchor')
      button.icon = anchorIcon
      button.keystroke = LINK_KEYSTROKE
      button.tooltip = true
      button.isToggleable = true

      // Bind button to the command.
      button.bind('isEnabled').to(anchorCommand, 'isEnabled')
      button.bind('isOn').to(anchorCommand, 'value', (value) => !!value)

      // Show the panel on button click.
      this.listenTo(button, 'execute', () => this._showUI(true))

      return button
    })
  }

  /**
   * Shows the correct UI type. It is either {@link #formView} or {@link #actionsView}.
   *
   * @param {Boolean} forceVisible
   * @private
   */
  _showUI(forceVisible = false) {
    // When there's no anchor under the selection, go straight to the editing UI.
    if (!this._getSelectedAnchorElement()) {
      // Show visual selection on a text without a anchor when the contextual balloon is displayed.
      // See https://github.com/ckeditor/ckeditor5/issues/4721.
      this._showFakeVisualSelection()

      this._addEditionView()

      // Be sure panel with anchor is visible.
      if (forceVisible) {
        this._balloon?.showStack('main')
      }

      this._addFormView()
    }
    // If there's a anchor under the selection...
    else {
      // Go to the editing UI if actions are already visible.
      if (this._isActionVisible) {
        this._addFormView()
      }
      // Otherwise display just the actions UI.
      else {
        this._addEditionView()
      }

      // Be sure panel with anchor is visible.
      if (forceVisible) {
        this._balloon?.showStack('main')
      }
    }

    // Begin responding to ui#update once the UI is added.
    this._startUpdatingUI()
  }

  _hideUI(): void {
    if (!this._isUIInPanel) {
      return
    }

    const editor = this.editor

    this.stopListening(editor.ui, 'update')
    this.stopListening(this._balloon, 'change:visibleView')

    // Make sure the focus always gets back to the editable _before_ removing the focused form view.
    // Doing otherwise causes issues in some browsers. See https://github.com/ckeditor/ckeditor5-anchor/issues/193.
    editor.editing.view.focus()

    // Remove form first because it's on top of the stack.
    this._removeFormView()

    // Then remove the actions view because it's beneath the form.
    this._balloon?.remove(this._actionView!)

    this._hideFakeVisualSelection()
  }

  /**
   * Makes the UI react to the {@link module:core/editor/editorui~EditorUI#event:update} event to
   * reposition itself when the editor UI should be refreshed.
   *
   * See: {@link #_hideUI} to learn when the UI stops reacting to the `update` event.
   */
  _startUpdatingUI() {
    const editor = this.editor
    const viewDocument = editor.editing.view.document

    let prevSelectedAnchor = this._getSelectedAnchorElement()
    let prevSelectionParent = getSelectionParent()

    const update = () => {
      const selectedAnchor = this._getSelectedAnchorElement()
      const selectionParent = getSelectionParent()

      // Hide the panel if:
      //
      // * the selection went out of the EXISTING anchor element. E.g. user moved the caret out
      //   of the anchor,
      // * the selection went to a different parent when creating a NEW anchor. E.g. someone
      //   else modified the document.
      // * the selection has expanded (e.g. displaying anchor actions then pressing SHIFT+Right arrow).
      //
      // Note: #_getSelectedAnchorElement will return a anchor for a non-collapsed selection only
      // when fully selected.
      if ((prevSelectedAnchor && !selectedAnchor) || (!prevSelectedAnchor && selectionParent !== prevSelectionParent)) {
        this._hideUI()
      }
      // Update the position of the panel when:
      //  * anchor panel is in the visible stack
      //  * the selection remains in the original anchor element,
      //  * there was no anchor element in the first place, i.e. creating a new anchor
      else if (this._isUIVisible) {
        // If still in a anchor element, simply update the position of the balloon.
        // If there was no anchor (e.g. inserting one), the balloon must be moved
        // to the new position in the editing view (a new native DOM range).
        this._balloon?.updatePosition(this._getBalloonPositionData())
      }

      prevSelectedAnchor = selectedAnchor
      prevSelectionParent = selectionParent
    }

    function getSelectionParent(): Node | DocumentFragment | undefined {
      return viewDocument.selection.focus
        ?.getAncestors()
        .reverse()
        .find((node) => node.is('element'))
    }

    this.listenTo(editor.ui, 'update', update)
    this.listenTo(this._balloon!, 'change:visibleView', update)
  }

  private _createFormView(): {
    disableCssTransitions(): void
    enableCssTransitions(): void
  } & AnchorFormView {
    const editor = this.editor
    const anchorCommand = editor.commands.get('anchor') as AnchorCommand

    const formView = new (CssTransitionDisablerMixin(AnchorFormView))(editor.locale)

    formView.urlInputView.fieldView.bind('value').to(anchorCommand, 'value')

    // Form elements should be read-only when corresponding commands are disabled.
    formView.saveButtonView.bind('isEnabled').to(anchorCommand)

    // Execute anchor command after clicking the "Save" button.
    this.listenTo(formView, 'submit', () => {
      const anchorId = (formView.urlInputView.fieldView.element as HTMLInputElement).value
      editor.execute('anchor', anchorId)
      this._closeFormView()
    })

    // Hide the panel after clicking the "Cancel" button.
    this.listenTo(formView, 'cancel', () => {
      this._closeFormView()
    })

    // Close the panel on esc key press when the **form has focus**.
    formView.keystrokes.set('Esc', (data, cancel) => {
      this._closeFormView()
      cancel()
    })

    return formView
  }

  /**
   * Adds the {@link #formView} to the {@link #_balloon}.
   */
  private _addFormView() {
    if (this._isFormInPanel) {
      return
    }

    const editor = this.editor
    const anchorCommand = editor.commands.get('anchor') as AnchorCommand
    const formView = this._formView!
    const balloon = this._balloon!

    formView.disableCssTransitions()

    balloon.add({
      view: formView,
      position: this._getBalloonPositionData(),
    })

    // Select input when form view is currently visible.
    if (balloon.visibleView === formView) {
      formView.urlInputView.fieldView.select()
    }

    formView.enableCssTransitions()

    // Make sure that each time the panel shows up, the URL field remains in sync with the value of
    // the command. If the user typed in the input, then canceled the balloon (`urlInputView.fieldView#value` stays
    // unaltered) and re-opened it without changing the value of the anchor command (e.g. because they
    // clicked the same anchor), they would see the old value instead of the actual value of the command.
    // https://github.com/ckeditor/ckeditor5-anchor/issues/78
    // https://github.com/ckeditor/ckeditor5-anchor/issues/123
    ;(formView.urlInputView.fieldView.element as HTMLInputElement).value = (anchorCommand.value as string) || ''
  }

  /**
   * Closes the form view. Decides whether the balloon should be hidden completely or if the action view should be shown. This is
   * decided upon the anchor command value (which has a value if the document selection is in the anchor).
   */
  private _closeFormView(): void {
    const anchorCommand = this.editor.commands.get('anchor') as AnchorCommand

    if (anchorCommand.value !== undefined) {
      this._removeFormView()
    } else {
      this._hideUI()
    }
  }

  _removeFormView(): void {
    if (this._isFormInPanel) {
      const formView = this._formView!

      // Blur the input element before removing it from DOM to prevent issues in some browsers.
      // See https://github.com/ckeditor/ckeditor5/issues/1501.
      formView.saveButtonView.focus()

      this._balloon?.remove(formView)

      // Because the form has an input which has focus, the focus must be brought back
      // to the editor. Otherwise, it would be lost.
      this.editor.editing.view.focus()

      this._hideFakeVisualSelection()
    }
  }

  private _createActionView() {
    const editor = this.editor
    const editingView = new AnchorActionView(editor.locale)
    const anchorCommand = editor.commands.get('anchor') as AnchorCommand
    const unanchorCommand = editor.commands.get('unanchor') as UnanchorCommand

    editingView.editButtonView.bind('isEnabled').to(anchorCommand)
    editingView.unanchorButtonView.bind('isEnabled').to(unanchorCommand)

    // Execute unanchor command after clicking on the "Edit" button.
    this.listenTo(editingView, 'edit', () => {
      this._addFormView()
    })

    // Execute unanchor command after clicking on the "Unanchor" button.
    this.listenTo(editingView, 'unanchor', () => {
      editor.execute('unanchor')
      this._hideUI()
    })

    // Close the panel on esc key press when the **actions have focus**.
    editingView.keystrokes.set('Esc', (data, cancel) => {
      this._hideUI()
      cancel()
    })

    // Open the form view on Ctrl+M when the **actions have focus**..
    editingView.keystrokes.set(LINK_KEYSTROKE, (data, cancel) => {
      this._addFormView()
      cancel()
    })

    return editingView
  }

  /**
   * Attaches actions that control whether the balloon panel containing the
   * {@link #formView} is visible or not.
   */
  private _enableUserBalloonInteractions(): void {
    const viewDocument = this.editor.editing.view.document

    // Handle click on view document and show panel when selection is placed inside the anchor element.
    // Keep panel open until selection will be inside the same anchor element.
    this.listenTo(viewDocument, 'click', () => {
      const parentAnchor = this._getSelectedAnchorElement()

      if (parentAnchor) {
        // Then show panel but keep focus inside editor editable.
        this._showUI()
      }
    })

    // Focus the form if the balloon is visible and the Tab key has been pressed.
    this.editor.keystrokes.set(
      'Tab',
      (data, cancel) => {
        const actionView = this._actionView!
        if (this._isActionVisible && !actionView.focusTracker.isFocused) {
          actionView.focus()
          cancel()
        }
      },
      {
        // Use the high priority because the anchor UI navigation is more important
        // than other feature's actions, e.g. list indentation.
        // https://github.com/ckeditor/ckeditor5-anchor/issues/146
        priority: 'high',
      },
    )

    // Close the panel on the Esc key press when the editable has focus and the balloon is visible.
    this.editor.keystrokes.set('Esc', (data, cancel) => {
      if (this._isUIVisible) {
        this._hideUI()
        cancel()
      }
    })

    // Close on click outside of balloon panel element.
    clickOutsideHandler({
      emitter: this._formView!,
      activator: () => this._isUIInPanel,
      contextElements: [this._balloon!.view.element!],
      callback: () => this._hideUI(),
    })
  }

  /**
   * Displays a fake visual selection when the contextual balloon is displayed.
   *
   * This adds a 'anchor-ui' marker into the document that is rendered as a highlight on selected text fragment.
   *
   * @private
   */
  private _showFakeVisualSelection(): void {
    const model = this.editor.model

    model.change((writer) => {
      const range = model.document.selection.getFirstRange()

      if (range === null) {
        return
      }

      if (model.markers.has(VISUAL_SELECTION_MARKER_NAME)) {
        writer.updateMarker(VISUAL_SELECTION_MARKER_NAME, { range })
      } else {
        if (range.start.isAtEnd) {
          const focus = model.document.selection.focus!
          const nextValidRange = getNextValidRange(range, focus, writer)

          writer.addMarker(VISUAL_SELECTION_MARKER_NAME, {
            usingOperation: false,
            affectsData: false,
            range: nextValidRange,
          })
        } else {
          writer.addMarker(VISUAL_SELECTION_MARKER_NAME, {
            usingOperation: false,
            affectsData: false,
            range,
          })
        }
      }
    })
  }

  _hideFakeVisualSelection(): void {
    const model = this.editor.model

    if (model.markers.has(VISUAL_SELECTION_MARKER_NAME)) {
      model.change((writer) => {
        writer.removeMarker(VISUAL_SELECTION_MARKER_NAME)
      })
    }
  }

  /**
   * Adds the {@link #_actionView} to the {@link #_balloon}.
   */
  private _addEditionView(): void {
    if (this._isActionInPanel || this._balloon === undefined || !this._actionView) {
      return
    }

    this._balloon.add({
      view: this._actionView,
      position: this._getBalloonPositionData(),
    })
  }

  private _getBalloonPositionData(): Options {
    const view = this.editor.editing.view
    const model = this.editor.model
    const viewDocument = view.document
    let target = null

    if (model.markers.has(VISUAL_SELECTION_MARKER_NAME)) {
      // There are cases when we highlight selection using a marker (#7705, #4721).
      const markerViewElements = Array.from(
        this.editor.editing.mapper.markerNameToElements(VISUAL_SELECTION_MARKER_NAME) ?? [],
      )
      const newRange = view.createRange(
        view.createPositionBefore(markerViewElements[0]),
        view.createPositionAfter(markerViewElements[markerViewElements.length - 1]),
      )

      target = view.domConverter.viewRangeToDom(newRange)
    } else {
      const targetAnchor = this._getSelectedAnchorElement()
      const range = viewDocument.selection.getFirstRange()!

      target = targetAnchor
        ? // When selection is inside anchor element, then attach panel to this element.
          view.domConverter.mapViewToDom(targetAnchor)
        : // Otherwise attach panel to the selection.
          view.domConverter.viewRangeToDom(range)
    }

    return { target } as Options
  }

  /**
   * Returns the anchor {@link module:engine/view/attributeelement~AttributeElement} under
   * the {@link module:engine/view/document~Document editing view's} selection or `null`
   * if there is none.
   *
   * **Note**: For a non–collapsed selection, the anchor element is only returned when **fully**
   * selected and the **only** element within the selection boundaries.
   */
  _getSelectedAnchorElement(): AttributeElement | undefined | null {
    const view = this.editor.editing.view
    const selection = view.document.selection

    if (selection.isCollapsed) {
      return findAnchorElementAncestor(selection.getFirstPosition()!)
    } else {
      // The range for fully selected anchor is usually anchored in adjacent text nodes.
      // Trim it to get closer to the actual anchor element.
      const range = selection.getFirstRange()?.getTrimmed()
      if (range === undefined) {
        return null
      }

      let item = undefined
      for (const entry of range.getWalker({ shallow: true }) as unknown as Array<TreeWalkerValue>) {
        if (item !== undefined) {
          return null
        }
        item = entry.item
      }

      if (item === undefined) {
        return null
      }

      const anchor = getAnchorElement(item)

      if (anchor === undefined) {
        return null
      }

      return anchor
    }
  }
}

function getAnchorElement(item: Item): AttributeElement | undefined {
  if (item.is('node')) {
    if (isAnchorElement(item)) {
      return item
    }
    return undefined
  } else {
    item.getAncestors().find(isAnchorElement)
  }
}

// Returns a anchor element if there's one among the ancestors of the provided `Position`.
function findAnchorElementAncestor(position: ViewPosition): AttributeElement | undefined {
  return position.getAncestors().find(isAnchorElement)
}

/**
 * Returns `true` if a given view node is the anchor element.
 */
export function isAnchorElement(node: Node | DocumentFragment): node is AttributeElement {
  return (node.is('attributeElement') || node.is('element')) && !!node.getCustomProperty('anchor')
}

// Returns next valid range for the fake visual selection marker.
function getNextValidRange(range: Range, focus: ModelPosition, writer: Writer): Range {
  const nextStartPath = [range.start.path[0] + 1, 0]
  const nextStartPosition = writer.createPositionFromPath(range.start.root, nextStartPath, 'toNext')
  const nextRange = writer.createRange(nextStartPosition, range.end)

  // Block creating a potential next valid range over the current range end.
  if (nextRange.start.path[0] > range.end.path[0]) {
    return writer.createRange(focus)
  }

  if (nextStartPosition.isAtStart && nextStartPosition.isAtEnd) {
    return getNextValidRange(nextRange, focus, writer)
  }

  return nextRange
}
